/* eslint-disable no-underscore-dangle */
import { FC } from 'react';
import { IAffiliationStatistics } from '@models/IAffiliationStatistics';
import {
  SupervisedUserCircle as SupervisedUserIcon,
  TextFormat as TextFormatIcon,
} from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import { Icon } from '@iconify/react';
import { useRouter } from 'next/router';
import { Loading } from '../../../../elements/Loading';
import { determineRiskType } from '../../../../elements/RiskIndicator/functions';
import { RiskLevel } from '../../../../../shared/types';
import {
  capitalizeFirstLetter,
  formatDate,
  getTextInitials,
} from '../../../../../shared/functions';
import { BreadcrumbModal } from '../../../../elements/BreadcrumbModal';

interface OrganizationSummaryProps {
  title: string;
  affiliationStats: IAffiliationStatistics;
  loading?: boolean;
}

export const OrganizationSummary: FC<OrganizationSummaryProps> = ({
  title,
  affiliationStats,
  loading,
}) => {
  const router = useRouter();
  const DIVIDER_SX = { my: 2 };

  return loading ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        gap: 1,
        px: 2,
        py: 1,
      }}
    >
      <Loading />
    </Box>
  ) : (
    <Box sx={{ px: 2.5, py: 1, minWidth: 280, maxWidth: 360 }}>
      <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', mb: 0.5 }}>
        <Tooltip title={title} followCursor>
          <Typography
            noWrap={title?.length > 100}
            variant={'subtitle1'}
            sx={{ maxWidth: 280, fontWeight: 600, color: '#e5e7eb' }}
          >
            {title}
          </Typography>
        </Tooltip>
        <Tooltip title={'Open visualization page'}>
          <IconButton
            sx={{ color: '#6366f1', '&:hover': { color: '#9fa8da' } }}
            onClick={() => {
              window.open(
                `/affiliation/${encodeURIComponent(title)}`,
                '_blank',
                'noopener noreferrer',
              );
            }}
          >
            <Icon icon="iconoir:open-new-window" style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {affiliationStats?.risk && (
        <Typography
          variant={'body2'}
          sx={{
            color: determineRiskType(affiliationStats?.risk).color,
            fontWeight:
              determineRiskType(affiliationStats?.risk).level ===
              RiskLevel.Extreme
                ? 700
                : 500,
            mb: 0.5,
          }}
        >
          {`${determineRiskType(affiliationStats?.risk).level} Risk`}
        </Typography>
      )}
      <Divider sx={{ ...DIVIDER_SX, borderColor: 'rgba(99,102,241,0.25)' }} />
      {affiliationStats?.maxYear && affiliationStats?.minYear && (
        <Typography variant={'body2'} sx={{ color: '#c5cae9' }}>
          {<b style={{ color: '#9fa8da' }}>Publications:&nbsp;</b>}
          {`${affiliationStats?.minYear}–${affiliationStats?.maxYear}`}
        </Typography>
      )}
      <Typography variant={'body2'} noWrap sx={{ color: '#c5cae9' }}>
        <b style={{ color: '#9fa8da' }}>Total Publications of Risk:&nbsp;</b>
        {affiliationStats?.publication_count}
      </Typography>
      <Divider sx={{ ...DIVIDER_SX, borderColor: 'rgba(99,102,241,0.25)' }} />
      {affiliationStats?.author_?.length > 0 && (
        <div>
          <Typography variant={'body2'} sx={{ color: '#9fa8da', mb: 0.5 }}>
            <b>People</b>
          </Typography>
          <BreadcrumbModal title={'People'} icon={<TextFormatIcon />}>
            {affiliationStats?.author_?.map((author_) => (
              <Typography
                key={`person${author_.name}`}
                variant={'body2'}
                sx={{ color: '#c5cae9' }}
              >
                {author_.name}
              </Typography>
            ))}
          </BreadcrumbModal>
        </div>
      )}{' '}
      <Box sx={{ mt: 1.5 }} />
      {affiliationStats?.authors?.length > 0 && (
        <div>
          <Typography variant={'body2'} sx={{ color: '#9fa8da', mb: 0.5 }}>
            <b>Authors</b>
          </Typography>
          <BreadcrumbModal title={'Authors'} icon={<TextFormatIcon />}>
            {affiliationStats?.authors?.map((author) => (
              <Typography
                key={`author${author}`}
                variant={'body2'}
                sx={{ color: '#c5cae9' }}
              >
                {author}
              </Typography>
            ))}
          </BreadcrumbModal>
        </div>
      )}
      <Box sx={{ mt: 1.5 }} />
      {affiliationStats?.associates?.length > 0 && (
        <div>
          <Typography variant={'body2'} sx={{ color: '#9fa8da', mb: 0.5 }}>
            <b>Associates</b>
          </Typography>
          <BreadcrumbModal title={'Associates'} icon={<SupervisedUserIcon />}>
            {affiliationStats?.associates?.map((associate) => (
              <Typography
                key={`associate${associate}`}
                variant={'body2'}
                sx={{ color: '#c5cae9' }}
              >
                {associate}
              </Typography>
            ))}
          </BreadcrumbModal>
        </div>
      )}
      {affiliationStats?.keywords && affiliationStats?.keywords?.length > 0 && (
        <Stack spacing={0.75}>
          <Typography variant={'body2'} sx={{ color: '#9fa8da' }}>
            <b>Keywords</b>
          </Typography>
          <Box
            sx={{
              maxHeight: 175,
              maxWidth: 320,
              overflowY: 'scroll',
              overflowX: 'hidden',
              borderRadius: 2,
              border: '1px solid rgba(99,102,241,0.25)',
              p: 1,
              backgroundColor: 'rgba(8,12,30,0.5)',
            }}
          >
            {affiliationStats?.keywords.map((keyword, i) => (
              <Chip
                key={keyword + i}
                variant={'outlined'}
                sx={{
                  mt: 0.75,
                  marginRight: 0.75,
                  borderColor: 'rgba(99,102,241,0.4)',
                  color: '#9fa8da',
                  '&&:hover': {
                    bgcolor: 'rgba(99,102,241,0.15)',
                    borderColor: '#6366f1',
                  },
                }}
                avatar={
                  <Avatar sx={{ bgcolor: 'rgba(99,102,241,0.3)' }}>
                    <Typography sx={{ color: '#c5cae9' }} variant={'caption'}>
                      {getTextInitials(keyword)}
                    </Typography>
                  </Avatar>
                }
                label={
                  <Typography
                    noWrap
                    variant={'body2'}
                    sx={{ color: '#c5cae9' }}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: capitalizeFirstLetter(keyword, true),
                      }}
                    />
                  </Typography>
                }
              />
            ))}
          </Box>
        </Stack>
      )}
    </Box>
  );
};
